export type AdminSeniorVoRole = "Senior VO" | "VO";
export type AdminSeniorVoStatus = "Active" | "Inactive";

export interface AdminSeniorVoMember {
  id: string;
  name: string;
  email: string;
  role: AdminSeniorVoRole;
  status: AdminSeniorVoStatus;
}

export interface AdminSeniorVoTeam {
  id: string;
  name: string;
  members: AdminSeniorVoMember[];
}

export interface AdminSeniorVoTeamsResponse {
  teams: AdminSeniorVoTeam[];
}

export interface AdminSeniorVoAddMemberPayload {
  teamId: string;
  teamName: string;
  memberName: string;
  memberEmail: string;
}

export interface AdminSeniorVoCreateTeamPayload {
  teamName: string;
  seniorVoName: string;
  seniorVoEmail: string;
}

export interface AdminSeniorVoUpdateMemberRolePayload {
  teamId: string;
  teamName: string;
  memberName: string;
  memberEmail: string;
  role: AdminSeniorVoRole;
}

export interface AdminSeniorVoUpdateMemberStatusPayload {
  teamId: string;
  teamName: string;
  memberName: string;
  memberEmail: string;
  isActive: boolean;
}

export interface AdminSeniorVoMutationResponse {
  success: boolean;
  message: string;
}

type LooseRecord = Record<string, unknown>;

export const normalizeAdminSeniorVoTeamsResponse = (payload: unknown): AdminSeniorVoTeamsResponse => {
  const flatTeamRows = getFlatTeamRows(payload);

  if (flatTeamRows.length > 0) {
    return {
      teams: normalizeFlatTeamRows(flatTeamRows),
    };
  }

  const teams = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as LooseRecord | null | undefined)?.teams)
      ? ((payload as LooseRecord).teams as unknown[])
      : [];

  return {
    teams: teams
      .map((team, index) => normalizeAdminSeniorVoTeam(team, index))
      .filter((team): team is AdminSeniorVoTeam => team !== null),
  };
};

const normalizeAdminSeniorVoTeam = (value: unknown, index: number): AdminSeniorVoTeam | null => {
  if (!isRecord(value)) {
    return null;
  }

  const members = getTeamMembers(value)
    .map((member, memberIndex) => normalizeAdminSeniorVoMember(member, memberIndex))
    .filter((member): member is AdminSeniorVoMember => member !== null);
  const seniorVoMember = members.find((member) => member.role === "Senior VO" && member.email);
  const name = getString(value, ["name", "teamName", "team_name", "TEAM_NAME"]) || `Team ${index + 1}`;
  const resolvedTeamIdentifier =
    getString(value, ["id", "teamId", "team_id", "uid", "teamUid", "team_uid", "TEAM_NAME"]) ||
    name ||
    seniorVoMember?.email ||
    `team-${index + 1}`;

  return {
    id: resolvedTeamIdentifier,
    name,
    members,
  };
};

const getTeamMembers = (value: LooseRecord): unknown[] => {
  const members = getArray(value, ["members", "teamMembers", "team_members", "users"]);

  if (members.length > 0) {
    return members;
  }

  return [
    ...getArray(value, ["seniorVoMembers", "senior_vo_members", "seniorVos", "senior_vos"]),
    ...getArray(value, ["voMembers", "vo_members", "vos"]),
  ];
};

const normalizeAdminSeniorVoMember = (value: unknown, index: number): AdminSeniorVoMember | null => {
  if (!isRecord(value)) {
    return null;
  }

  const firstName = getString(value, ["firstName", "first_name"]);
  const lastName = getString(value, ["lastName", "last_name"]);
  const combinedName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const name = combinedName || getString(value, ["name", "displayName", "display_name", "fullName", "full_name"]);

  return {
    id: getString(value, ["id", "userId", "user_id", "uid"]) || `member-${index + 1}`,
    name: name || "Unknown User",
    email: getString(value, ["email", "mail", "userEmail", "user_email"]),
    role: normalizeRole(
      getString(value, ["role", "memberRole", "member_role", "type", "userType", "user_type", "MEMBER_ROLE"]),
    ),
    status: normalizeStatus(getString(value, ["status", "memberStatus", "member_status", "state"])),
  };
};

const normalizeRole = (value: string): AdminSeniorVoRole => {
  const normalized = value.trim().toLowerCase().replaceAll("_", " ").replaceAll("-", " ");

  if (normalized.includes("senior")) {
    return "Senior VO";
  }

  return "VO";
};

const normalizeStatus = (value: string): AdminSeniorVoStatus => {
  const normalized = value.trim().toLowerCase();
  return normalized === "inactive" || normalized === "disabled" ? "Inactive" : "Active";
};

const getArray = (value: LooseRecord, keys: string[]): unknown[] => {
  for (const key of keys) {
    const candidate = value[key];

    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const getBoolean = (value: LooseRecord, keys: string[]): boolean => {
  for (const key of keys) {
    const candidate = value[key];

    if (typeof candidate === "boolean") {
      return candidate;
    }

    if (typeof candidate === "string") {
      const normalized = candidate.trim().toLowerCase();

      if (normalized === "true" || normalized === "yes" || normalized === "y") {
        return true;
      }
    }

    if (typeof candidate === "number" && candidate === 1) {
      return true;
    }
  }

  return false;
};

const getString = (value: LooseRecord, keys: string[]): string => {
  for (const key of keys) {
    const candidate = value[key];

    if (typeof candidate === "string") {
      return candidate.trim();
    }

    if (typeof candidate === "number") {
      return String(candidate);
    }
  }

  return "";
};

const isRecord = (value: unknown): value is LooseRecord => typeof value === "object" && value !== null;

export const normalizeAdminSeniorVoMutationResponse = (
  payload: unknown,
  fallbackSuccess = true,
): AdminSeniorVoMutationResponse => {
  if (typeof payload === "string") {
    return {
      success: fallbackSuccess,
      message: payload.trim() || getDefaultAdminSeniorVoMutationMessage(fallbackSuccess),
    };
  }

  if (isRecord(payload)) {
    const successCandidate = getBoolean(payload, ["success", "ok", "isSuccess", "is_success"]);
    const errorCandidate = getBoolean(payload, ["error", "hasError", "has_error"]);
    const message =
      getString(payload, ["message", "msg", "detail", "statusMessage", "status_message", "errorMessage"]) ||
      getDefaultAdminSeniorVoMutationMessage(!errorCandidate && (successCandidate || fallbackSuccess));

    return {
      success: errorCandidate ? false : successCandidate || fallbackSuccess,
      message,
    };
  }

  return {
    success: fallbackSuccess,
    message: getDefaultAdminSeniorVoMutationMessage(fallbackSuccess),
  };
};

const getDefaultAdminSeniorVoMutationMessage = (success: boolean) =>
  success ? "Member request completed successfully." : "Member request failed.";

const getFlatTeamRows = (payload: unknown): LooseRecord[] => {
  const rawRows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as LooseRecord | null | undefined)?.teams)
      ? ((payload as LooseRecord).teams as unknown[])
      : [];

  return rawRows.filter((row): row is LooseRecord => {
    if (!isRecord(row)) {
      return false;
    }

    const teamName = getString(row, ["TEAM_NAME", "teamName", "team_name", "name"]);
    const role = getString(row, ["MEMBER_ROLE", "memberRole", "member_role", "role"]);
    const email = getString(row, ["MEMBER_EMAIL", "memberEmail", "member_email", "email"]);
    const hasNestedMembers = getArray(row, ["members", "teamMembers", "team_members", "users"]).length > 0;
    return Boolean(teamName && role && email && !hasNestedMembers);
  });
};

const normalizeFlatTeamRows = (rows: LooseRecord[]): AdminSeniorVoTeam[] => {
  const teamsByName = new Map<string, AdminSeniorVoTeam>();

  rows.forEach((row, index) => {
    const teamName = getString(row, ["TEAM_NAME", "teamName", "team_name", "name"]);
    const teamId = teamName || getString(row, ["id", "teamId", "team_id"]) || `team-${index + 1}`;
    const member = normalizeAdminSeniorVoMember(
      {
        ...row,
        id: getString(row, ["id", "memberId", "member_id"]) || `${teamId}-${index + 1}`,
        name: getString(row, ["MEMBER_NAME", "memberName", "member_name", "name"]),
        email: getString(row, ["MEMBER_EMAIL", "memberEmail", "member_email", "email"]),
        role: getString(row, ["MEMBER_ROLE", "memberRole", "member_role", "role"]),
        status: getString(row, ["status", "memberStatus", "member_status"]) || (getBoolean(row, ["IS_ACTIVE"]) ? "Active" : "Inactive"),
      },
      index,
    );

    if (!teamName || !member) {
      return;
    }

    const existing = teamsByName.get(teamId);

    if (!existing) {
      teamsByName.set(teamId, {
        id: teamId,
        name: teamName,
        members: [member],
      });
      return;
    }

    existing.members.push(member);
  });

  return Array.from(teamsByName.values());
};
